import React, { useState, useEffect } from 'react';
import {
    cancelFriendRequest,
    sendFriendRequest,
    removeFriend,
    serverTimestamp, // Added
    getUserProfile,
    getUserSparks,
    subscribeToUserSparks,
    getMagicSparkHolder,
    getFriendRequests,
    subscribeToFriendRequests,
    subscribeToSentFriendRequests,
    subscribeToFriends,

    getFriends,
    reportUser,
    subscribeToUserProfile,
    updateUserProfile, // Added
    addSubscriptionDays,
    sendDirectGiftCode,
    likeSpark,
    reactToSpark,
    resetTutorialSeen,
    BADGES
} from '../services/firestore.js';
import { syncStripeCustomer, createGiftCheckoutSession } from '../services/stripe.js';
import { Share2, MessageCircle, Heart, MoreHorizontal, Flag, AlertTriangle, X, Shield, Users, Award, Zap, Flame, Sparkles, Trophy, Lightbulb, Shuffle, Twitter, Instagram, Youtube, Globe, Music, Video, Github, Linkedin, Facebook, Gamepad2, Gift, ArrowLeft, UserPlus, Check, XCircle, Plus, Clock, Smile, Coffee, Crown, UserMinus, Rocket } from 'lucide-react';
import UserBadge from '../components/UserBadge.jsx';
import EmotePicker from '../components/EmotePicker.jsx';
import EmojiReactionBar from '../components/EmojiReactionBar.jsx';
import { useEmoteTheme } from '../hooks/useEmoteTheme.js';
import { parseContentWithEmotes } from '../utils/textUtils.js';

const SOCIAL_ICONS = {
    twitter: Twitter,
    instagram: Instagram,
    youtube: Youtube,
    twitch: Gamepad2,
    tiktok: Video,
    github: Github,
    linkedin: Linkedin,
    facebook: Facebook,
    discord: MessageCircle,
    spotify: Music,
    portfolio: Globe,
    website: Globe
};

export default function PublicProfile({ userId, onBack, user: currentUser }) {
    const [profileUser, setProfileUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [friendStatus, setFriendStatus] = useState('none');
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [adminTier, setAdminTier] = useState(null); // For Admin Dropdown
    const [showGiftModal, setShowGiftModal] = useState(false); // New Modal state
    const [userSparks, setUserSparks] = useState([]);
    const [magicSparkHolder, setMagicSparkHolder] = useState(null);
    const [friendshipId, setFriendshipId] = useState(null);
    const [hoverRequest, setHoverRequest] = useState(false);

    // State for subscriptions
    const [incomingRequests, setIncomingRequests] = useState([]);
    const [sentRequests, setSentRequests] = useState([]);
    const [activeFriends, setActiveFriends] = useState([]);

    // Emoji reaction state
    const [showEmojiPicker, setShowEmojiPicker] = useState(null);
    const [sparkReactions, setSparkReactions] = useState({});

    // Theme Hook
    const { getEmoteObject, checkEmoteAccess, getEmoteUrl } = useEmoteTheme();

    // Subscriptions
    useEffect(() => {
        if (!currentUser?.uid || !profileUser?.id) return;

        // 1. Sent Requests (I sent to them)
        const unsubSent = subscribeToSentFriendRequests(currentUser.uid, setSentRequests);

        // 2. Incoming Requests (They sent to me)
        const unsubIncoming = subscribeToFriendRequests(currentUser.uid, setIncomingRequests);

        // 3. Active Friends
        const unsubFriends = subscribeToFriends(currentUser.uid, setActiveFriends);

        return () => {
            unsubSent();
            unsubIncoming();
            unsubFriends();
        };
    }, [currentUser?.uid, profileUser?.id]);

    // Derived Status
    useEffect(() => {
        if (!currentUser || !profileUser) return;

        if (currentUser.uid === profileUser.id) {
            setFriendStatus('self');
            return;
        }

        // Check if ALREADY friends
        // activeFriends now contains user profiles with an injected 'friendshipId' property
        const friendDoc = activeFriends.find(f => f.id === profileUser.id);
        if (friendDoc) {
            setFriendStatus('friend');
            setFriendshipId(friendDoc.friendshipId);
            return;
        }

        // Check if I SENT a request
        const sentReq = sentRequests.find(r => r.to === profileUser.id);
        if (sentReq) {
            setFriendStatus('pending');
            return;
        }

        setFriendStatus('none');
        setFriendshipId(null);

    }, [currentUser, profileUser, activeFriends, sentRequests, incomingRequests]);

    useEffect(() => {
        let unsubscribeSparks = null;
        let unsubscribeProfile = null;

        if (!userId) return;
        setLoading(true);

        const setupSubscriptions = async () => {
            try {
                // 1. Real-time User Profile Subscription
                unsubscribeProfile = subscribeToUserProfile(userId, (user) => {
                    setProfileUser(user);
                    setLoading(false); // Stop loading once we have data
                });

                // 2. Real-time Sparks Subscription
                unsubscribeSparks = subscribeToUserSparks(userId, (sparks) => {
                    setUserSparks(sparks);
                    // Sync reactions to local state
                    const reactions = {};
                    for (const spark of sparks) {
                        if (spark.reactions) {
                            reactions[spark.id] = spark.reactions;
                        }
                    }
                    setSparkReactions(reactions);
                });

                // 3. Check Magic Spark
                const magic = await getMagicSparkHolder();
                if (magic) setMagicSparkHolder(magic.currentHolder);

            } catch (error) {
                console.error("Error setting up subscriptions:", error);
                setLoading(false);
            }
        };

        setupSubscriptions();

        return () => {
            if (unsubscribeSparks) unsubscribeSparks();
            if (unsubscribeProfile) unsubscribeProfile();
        };
    }, [userId]);

    const handleAddFriend = async () => {
        if (!currentUser || !profileUser) return;
        try {
            await sendFriendRequest(currentUser.uid, currentUser.name, currentUser.avatar, profileUser.uid, profileUser.name);
            // Subscription will update state
        } catch (e) {
            console.error(e);
        }
    };

    const handleCancelRequest = async () => {
        if (!currentUser || !profileUser) return;
        try {
            await cancelFriendRequest(currentUser.uid, profileUser.uid);
            // Subscription will update state
            setHoverRequest(false);
        } catch (error) {
            console.error("Error cancelling friend request:", error);
        }
    };

    const handleRemoveFriend = async () => {
        if (!confirmRemove) {
            setConfirmRemove(true);
            setTimeout(() => setConfirmRemove(false), 3000);
            return;
        }
        if (friendshipId) {
            try {
                // Remove friend and remove from any private chats I host
                await removeFriend(friendshipId, currentUser.uid, profileUser.id);
                // Subscription will update state
                setFriendshipId(null);
            } catch (e) { console.error(e); }
        }
    };

    const handleReport = async () => {
        if (!currentUser || !profileUser) return;
        const reason = window.prompt("Found something concerning? Please briefly tell us why you are reporting this user:");
        if (reason) {
            try {
                await reportUser(currentUser.uid, profileUser.id, reason);
                alert("Report submitted. Thank you for helping keep our community safe.");
            } catch (e) {
                console.error("Report failed:", e);
                alert("Failed to submit report. Please try again.");
            }
        }
    };

    const handleLikeSpark = async (spark) => {
        if (!currentUser) return;
        try {
            await likeSpark(spark.id, currentUser.uid);
        } catch (error) {
            console.error("Error liking spark:", error);
        }
    };

    const handleShareSpark = (spark) => {
        const text = `Check out this Spark by ${spark.author}: "${spark.content || 'Art'}"`;
        if (navigator.share) {
            navigator.share({
                title: 'Spark Inspiration',
                text: text,
                url: window.location.href
            }).catch(console.error);
        } else {
            alert("Share feature is simulated for this environment.");
        }
    };

    // Emoji Reaction Handlers
    const handleOpenEmojiPicker = (sparkId) => {
        setShowEmojiPicker(sparkId);
    };

    const handleCloseEmojiPicker = () => {
        setShowEmojiPicker(null);
    };

    const handleEmojiSelect = async (sparkId, emojiId) => {
        if (!currentUser?.uid) return;
        setSparkReactions(prev => {
            const sparkReacts = { ...(prev[sparkId] || {}) };
            const currentReactors = sparkReacts[emojiId] || [];
            const hasReacted = currentReactors.includes(currentUser.uid);
            if (hasReacted) {
                sparkReacts[emojiId] = currentReactors.filter(id => id !== currentUser.uid);
            } else {
                sparkReacts[emojiId] = [...currentReactors, currentUser.uid];
            }
            return { ...prev, [sparkId]: sparkReacts };
        });
        try {
            await reactToSpark(sparkId, emojiId, currentUser.uid);
        } catch (error) {
            console.error('Error reacting to spark:', error);
        }
    };

    const handleReactionToggle = async (sparkId, emojiId) => {
        if (!currentUser?.uid) return;
        setSparkReactions(prev => {
            const sparkReacts = { ...(prev[sparkId] || {}) };
            const currentReactors = sparkReacts[emojiId] || [];
            const hasReacted = currentReactors.includes(currentUser.uid);
            if (hasReacted) {
                sparkReacts[emojiId] = currentReactors.filter(id => id !== currentUser.uid);
            } else {
                sparkReacts[emojiId] = [...currentReactors, currentUser.uid];
            }
            return { ...prev, [sparkId]: sparkReacts };
        });
        try {
            await reactToSpark(sparkId, emojiId, currentUser.uid);
        } catch (error) {
            console.error('Error toggling reaction:', error);
        }
    };

    // Admin Menu State
    const [showAdminMenu, setShowAdminMenu] = useState(false);

    const handleUpdateTier = async (tier) => {
        if (!profileUser?.id) return;
        try {
            if (tier === 'global') {
                // Manual downgrade / Clear
                const updates = {
                    subscriptionTier: 'global',
                    subscriptionStatus: 'none',
                    subscriptionEnd: null,
                    subscriberEnd: null,
                    superSubscriberEnd: null,
                    giftNotificationPending: false
                };
                await updateUserProfile(profileUser.id, updates);
                alert("User reset to Global (Free) tier.");
            } else {
                // Use the Stacking Logic Helper
                await addSubscriptionDays(profileUser.id, tier, 30);
                alert(`Gifted ${tier === 'super_subscriber' ? 'Super Fan' : 'Supporter'} tier for 30 days! (Stacked)`);
            }

            setShowAdminMenu(false);
        } catch (error) {
            console.error("Error updating tier:", error);
            alert("Failed to update tier.");
        }
    };

    if (loading) {
        return React.createElement('div', { style: { textAlign: 'center', padding: '50px' } }, "Loading...");
    }

    if (!profileUser) {
        return React.createElement('div', { style: { textAlign: 'center', padding: '50px' } }, "User not found");
    }

    // Merge system badges with user unlocks (STRICT DISPLAY - Source of Truth is DB)
    const displayBadges = BADGES.filter(badge => {
        // MYSTERY LOGIC: 'vip_gold_crown' only visible if the TARGET USER is a Mega Fan
        if (badge.id === 'vip_gold_crown' && profileUser.subscriptionTier !== 'mega_fan') return false;
        return true;
    }).map(badgeDef => {
        const unlockedBadge = profileUser.badges?.find(b => b.id === badgeDef.id);

        return {
            ...badgeDef,
            unlocked: unlockedBadge?.unlocked || false,
            unlockedAt: unlockedBadge?.unlockedAt
        };
    });

    const iconMap = {
        Zap: Zap,
        Flame: Flame,
        Sparkles: Sparkles,
        Award: Award,
        Users: Users,
        Trophy: Trophy,
        Lightbulb: Lightbulb,
        Crown: Crown,
        Rocket: Rocket
    };

    return React.createElement('div', { className: 'animate-fade-in', style: { maxWidth: '800px', margin: '0 auto' } },
        // Back button
        React.createElement('button', {
            className: 'btn',
            onClick: onBack,
            style: { marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }
        }, React.createElement(ArrowLeft, { size: 18 }), "Back"),

        // User Header
        React.createElement('div', { className: 'glass-panel', style: { padding: '30px', marginBottom: '20px', textAlign: 'center', position: 'relative', overflow: 'visible', zIndex: 5 } }, // overflow: visible for dropdown
            // Background decoration
            React.createElement('div', { style: { position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)', zIndex: 0, pointerEvents: 'none' } }),

            // SUPER ADMIN CONTROLS (Gold Shield)
            currentUser?.isSuperAdmin && React.createElement('div', { style: { position: 'absolute', top: '20px', right: '20px', zIndex: 50 } },
                React.createElement('button', {
                    onClick: () => setShowAdminMenu(!showAdminMenu),
                    title: "Super Admin Controls",
                    style: {
                        background: 'linear-gradient(135deg, #ffd700, #f59e0b)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
                        transition: 'transform 0.2s',
                        transform: showAdminMenu ? 'scale(1.1)' : 'scale(1)'
                    }
                }, React.createElement(Shield, { size: 18, color: '#fff', fill: '#fff' })),

                // Admin Dropdown
                showAdminMenu && React.createElement('div', {
                    className: 'animate-fade-in',
                    style: {
                        position: 'absolute',
                        top: '45px',
                        right: '0',
                        background: '#1a1a2e',
                        border: '1px solid var(--border)',
                        borderRadius: '12px',
                        padding: '10px',
                        width: '200px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                        zIndex: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                    }
                },
                    React.createElement('div', { style: { fontSize: '11px', color: '#999', textTransform: 'uppercase', marginBottom: '4px', textAlign: 'left', paddingLeft: '4px' } }, "Debug"),
                    React.createElement('button', {
                        onClick: async () => {
                            if (window.confirm("Reset tutorial for this user? They will see it immediately or on next refresh.")) {
                                await resetTutorialSeen(profileUser.id);
                                alert("Tutorial reset! 🎓");
                            }
                        },
                        style: {
                            padding: '8px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#fff',
                            fontSize: '13px',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '10px'
                        },
                        onMouseEnter: (e) => { e.target.style.background = 'rgba(255,255,255,0.1)'; },
                        onMouseLeave: (e) => { e.target.style.background = 'rgba(255,255,255,0.05)'; }
                    }, "🎓 Replay Tutorial"),

                    React.createElement('button', {
                        onClick: async () => {
                            if (window.confirm("Force sync this user to Stripe? (Use if payments fail)")) {
                                try {
                                    await syncStripeCustomer(profileUser.id, profileUser.email || 'no-email@placeholder.com', profileUser.name);
                                    alert("Stripe Sync Triggered! 💳 Try payment again.");
                                } catch (err) {
                                    console.error(err);
                                    alert("Sync failed: " + err.message);
                                }
                            }
                        },
                        style: {
                            padding: '8px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#fbbf24',
                            fontSize: '13px',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '10px'
                        },
                        onMouseEnter: (e) => { e.target.style.background = 'rgba(255,255,255,0.1)'; },
                        onMouseLeave: (e) => { e.target.style.background = 'rgba(255,255,255,0.05)'; }
                    }, "💳 Force Stripe Sync"),

                    React.createElement('div', { style: { fontSize: '11px', color: '#999', textTransform: 'uppercase', marginBottom: '4px', textAlign: 'left', paddingLeft: '4px' } }, "Gift Subscription"),
                    React.createElement('button', {
                        onClick: () => handleUpdateTier('global'),
                        style: {
                            padding: '8px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid transparent',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#ccc',
                            fontSize: '13px',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        },
                        onMouseEnter: (e) => { e.target.style.background = 'rgba(255,255,255,0.1)'; },
                        onMouseLeave: (e) => { e.target.style.background = 'rgba(255,255,255,0.05)'; }
                    }, "🎁 Set to Free"),
                    React.createElement('button', {
                        onClick: () => handleUpdateTier('subscriber'),
                        style: {
                            padding: '8px',
                            background: 'rgba(236, 72, 153, 0.1)',
                            border: '1px solid rgba(236, 72, 153, 0.3)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#ec4899',
                            fontSize: '13px',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            justifyContent: 'space-between',
                            width: '100%'
                        },
                        onMouseEnter: (e) => { e.target.style.background = 'rgba(236, 72, 153, 0.2)'; },
                        onMouseLeave: (e) => { e.target.style.background = 'rgba(236, 72, 153, 0.1)'; }
                    },
                        React.createElement('span', {}, "🎁 Gift Supporter"),
                        profileUser.subscriberEnd && React.createElement('span', { style: { fontSize: '10px', opacity: 0.7 } },
                            `Valid until: ${new Date(profileUser.subscriberEnd.seconds * 1000).toLocaleDateString()}`
                        )
                    ),
                    React.createElement('button', {
                        onClick: () => handleUpdateTier('super_subscriber'),
                        style: {
                            padding: '8px',
                            background: 'rgba(139, 92, 246, 0.1)',
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#8b5cf6',
                            fontSize: '13px',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            justifyContent: 'space-between',
                            width: '100%'
                        },
                        onMouseEnter: (e) => { e.target.style.background = 'rgba(139, 92, 246, 0.2)'; },
                        onMouseLeave: (e) => { e.target.style.background = 'rgba(139, 92, 246, 0.1)'; }
                    },
                        React.createElement('span', {}, "🎁 Gift Super Fan"),
                        profileUser.superSubscriberEnd && React.createElement('span', { style: { fontSize: '10px', opacity: 0.7 } },
                            `Valid until: ${new Date(profileUser.superSubscriberEnd.seconds * 1000).toLocaleDateString()}`
                        )
                    )
                )
            ),

            React.createElement('img', {
                src: profileUser.avatar,
                style: { width: '100px', height: '100px', borderRadius: '50%', border: '3px solid var(--primary)', marginBottom: '15px', position: 'relative', zIndex: 1, objectFit: 'cover' }
            }),
            React.createElement('h2', { style: { fontSize: '28px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', position: 'relative', zIndex: 1 } },
                profileUser.name,
                React.createElement(UserBadge, { user: profileUser, size: 40 }),
                friendStatus === 'friend' && React.createElement('span', {
                    title: "Friend",
                    className: "animate-fade-in",
                    style: {
                        background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 2px 10px rgba(139, 92, 246, 0.3)',
                        fontWeight: '600'
                    }
                }, React.createElement(Sparkles, { size: 12, fill: "white" }), "Friend")
            ),
            React.createElement('p', { style: { color: '#999', marginBottom: '20px', position: 'relative', zIndex: 1, whiteSpace: 'pre-wrap' } },
                profileUser.bio ? parseContentWithEmotes(profileUser.bio, getEmoteUrl) : "No bio yet."
            ),

            // Social Links - Dynamic Render
            React.createElement('div', { style: { display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '20px', position: 'relative', zIndex: 1, flexWrap: 'wrap' } },
                (() => {
                    const links = profileUser.links || (profileUser.socialLinks ? Object.entries(profileUser.socialLinks).map(([k, v]) => ({ platform: k, url: v })) : []);
                    return links.map((link, i) => {
                        if (!link.url) return null;
                        const PlatformIcon = SOCIAL_ICONS[link.platform] || Globe;
                        return React.createElement('a', {
                            key: i,
                            href: link.url,
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            className: `social-link ${link.platform}`
                        }, React.createElement(PlatformIcon, { size: 20 }));
                    });
                })()
            ),

            // Badges Section
            React.createElement('div', { style: { marginBottom: '20px', position: 'relative', zIndex: 1 } },
                React.createElement('h3', { style: { fontSize: '14px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' } }, "Achievements"),
                React.createElement('div', { style: { display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' } },
                    displayBadges.map(badge => {
                        const IconComponent = iconMap[badge.icon] || Award;
                        return React.createElement('div', {
                            key: badge.id,
                            title: badge.unlocked ? badge.name : "Locked",
                            style: {
                                width: '50px',
                                height: '50px',
                                borderRadius: '12px',
                                background: badge.unlocked ? `rgba(${parseInt(badge.color.slice(1, 3), 16)}, ${parseInt(badge.color.slice(3, 5), 16)}, ${parseInt(badge.color.slice(5, 7), 16)}, 0.1)` : 'rgba(255,255,255,0.03)',
                                border: badge.unlocked ? `1px solid ${badge.color}` : '1px solid transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: badge.unlocked ? 1 : 0.3,
                                transition: 'all 0.2s',
                                cursor: 'help'
                            }
                        },
                            React.createElement(IconComponent, { size: 24, color: badge.unlocked ? badge.color : '#666' })
                        );
                    }),
                    // Magic Spark Badge
                    magicSparkHolder === userId && React.createElement('div', {
                        key: 'magic_spark',
                        title: '✨ Magic Spark Holder',
                        className: 'animate-fade-in',
                        style: {
                            width: '50px',
                            height: '50px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #ff0080, #ff8c00, #40e0d0, #ff0080)',
                            backgroundSize: '300% 300%',
                            animation: 'gradient 3s ease infinite',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'help',
                            boxShadow: '0 0 15px rgba(255, 0, 128, 0.4)'
                        }
                    },
                        React.createElement(Sparkles, { size: 24, color: 'white', fill: 'white' })
                    )
                )
            ),

            // Friend Actions
            friendStatus !== 'self' && React.createElement('div', { style: { marginBottom: '20px', display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center', position: 'relative', zIndex: 999 } },
                friendStatus === 'none' && React.createElement('button', {
                    className: 'btn',
                    onClick: handleAddFriend,
                    style: { padding: '8px 16px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }
                }, React.createElement(UserPlus, { size: 16 }), "Add Friend"),

                friendStatus === 'pending' && React.createElement('button', {
                    className: 'btn-ghost',
                    onClick: handleCancelRequest,
                    onMouseEnter: () => setHoverRequest(true),
                    onMouseLeave: () => setHoverRequest(false),
                    style: {
                        padding: '8px 16px',
                        fontSize: '14px',
                        color: hoverRequest ? '#ef4444' : '#9CA3AF', // Red on hover
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: hoverRequest ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        transition: 'all 0.2s'
                    }
                }, React.createElement(hoverRequest ? XCircle : Clock, { size: 16 }), hoverRequest ? "Cancel Request" : "Request Sent"),

                friendStatus === 'friend' && React.createElement('button', {
                    onClick: handleRemoveFriend,
                    title: confirmRemove ? "Click to Confirm" : "Remove Friend",
                    style: {
                        background: confirmRemove ? 'var(--secondary)' : 'none',
                        border: confirmRemove ? 'none' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        color: confirmRemove ? 'white' : '#999',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: confirmRemove ? 'bold' : 'normal',
                        transition: 'all 0.2s',
                        minWidth: '90px'
                    },
                    onMouseEnter: (e) => !confirmRemove && (e.target.style.borderColor = 'var(--border)'),
                    onMouseLeave: (e) => !confirmRemove && (e.target.style.borderColor = 'rgba(255,255,255,0.1)')
                }, confirmRemove ? "Sure?" : [React.createElement(UserMinus, { key: 'icon', size: 14 }), "Unfriend"]),

                // Send a Gift Button
                React.createElement('button', {
                    className: 'btn',
                    onClick: () => setShowGiftModal(true),
                    style: {
                        padding: '8px 16px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'linear-gradient(135deg, #FFD700, #FDB931)', // Gold gradient
                        color: 'black',
                        border: 'none',
                        fontWeight: 'bold'
                    }
                }, React.createElement(Gift, { size: 16, color: 'black' }), "Send Gift"),

                // CLICK OUTSIDE LAYER
                showGiftModal && React.createElement('div', {
                    style: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 998, cursor: 'default' },
                    onClick: (e) => { e.stopPropagation(); setShowGiftModal(false); }
                }),

                // GIFT POPOVER
                showGiftModal && React.createElement('div', {
                    className: 'glass-panel',
                    onClick: e => e.stopPropagation(),
                    style: {
                        position: 'absolute',
                        top: '110%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '320px',
                        padding: '0',
                        border: '1px solid #fbbf24',
                        background: 'var(--bg-dark)', // Solid background
                        color: 'var(--text-main)',
                        zIndex: 999,
                        boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                    }
                },
                    // Header
                    React.createElement('div', { style: { padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(0,0,0,0))' } },
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', color: '#fbbf24', fontWeight: 'bold' } },
                            React.createElement(Gift, { size: 20, color: '#fbbf24' }),
                            "Pay it Forward"
                        ),
                        React.createElement('button', {
                            onClick: () => setShowGiftModal(false),
                            style: { background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }
                        }, React.createElement(X, { size: 20 }))
                    ),

                    // Content
                    React.createElement('div', { style: { padding: '25px' } },
                        React.createElement('div', { style: { textAlign: 'center', marginBottom: '25px' } },
                            React.createElement('div', { style: { fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '5px' } }, "Send a gift to"),
                            React.createElement('div', { style: { fontSize: '20px', fontWeight: 'bold', color: 'var(--text-primary)' } }, profileUser.name),
                            React.createElement('div', { style: { fontSize: '12px', color: 'var(--text-secondary)', marginTop: '5px' } }, "Give them the gift of inspiration!")
                        ),

                        React.createElement('div', { style: { display: 'grid', gap: '15px' } },
                            // Supporter
                            React.createElement('button', {
                                onClick: async () => {
                                    console.log("Gift Supporter Clicked");
                                    if (!currentUser) {
                                        alert("Please sign in to send gifts!");
                                        return;
                                    }
                                    // Removed window.confirm to fix blocking issue
                                    try {
                                        console.log("Initiating checkout session...");
                                        const url = await createGiftCheckoutSession(currentUser.uid, 'supporter', profileUser.id, profileUser.name);
                                        console.log("Checkout URL generated:", url);
                                        if (url) window.location.assign(url);
                                    } catch (err) {
                                        console.error("Gift Checkout Error:", err);
                                        alert("Failed to start checkout. " + err.message);
                                    }
                                },
                                style: {
                                    padding: '15px',
                                    background: 'rgba(236, 72, 153, 0.1)',
                                    border: '1px solid rgba(236, 72, 153, 0.3)',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    transition: 'all 0.2s'
                                },
                                onMouseEnter: (e) => { e.currentTarget.style.background = 'rgba(236, 72, 153, 0.2)'; e.currentTarget.style.transform = 'translateY(-2px)' },
                                onMouseLeave: (e) => { e.currentTarget.style.background = 'rgba(236, 72, 153, 0.1)'; e.currentTarget.style.transform = 'translateY(0)' }
                            },
                                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                                    React.createElement(Coffee, { size: 24, color: '#ec4899' }),
                                    React.createElement('div', { style: { textAlign: 'left' } },
                                        React.createElement('div', { style: { fontWeight: 'bold', color: '#ec4899' } }, "Gift Supporter"),
                                        React.createElement('div', { style: { fontSize: '11px', color: 'rgba(236, 72, 153, 0.8)' } }, "30 Days Access")
                                    )
                                ),
                                React.createElement('div', { style: { fontWeight: 'bold', color: 'var(--text-main)' } }, "$5.00")
                            ),

                            // Super Fan
                            React.createElement('button', {
                                onClick: async () => {
                                    console.log("Gift Super Fan Clicked");
                                    if (!currentUser) {
                                        alert("Please sign in to send gifts!");
                                        return;
                                    }
                                    // Removed window.confirm to fix blocking issue
                                    try {
                                        console.log("Initiating checkout session...");
                                        const url = await createGiftCheckoutSession(currentUser.uid, 'super_fan', profileUser.id, profileUser.name);
                                        console.log("Checkout URL generated:", url);
                                        if (url) window.location.assign(url);
                                    } catch (err) {
                                        console.error("Gift Checkout Error:", err);
                                        alert("Failed to start checkout. " + err.message);
                                    }
                                },
                                style: {
                                    padding: '15px',
                                    background: 'rgba(139, 92, 246, 0.1)',
                                    border: '1px solid rgba(139, 92, 246, 0.3)',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    transition: 'all 0.2s'
                                },
                                onMouseEnter: (e) => { e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)'; e.currentTarget.style.transform = 'translateY(-2px)' },
                                onMouseLeave: (e) => { e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)'; e.currentTarget.style.transform = 'translateY(0)' }
                            },
                                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                                    React.createElement(Crown, { size: 24, color: '#8b5cf6' }),
                                    React.createElement('div', { style: { textAlign: 'left' } },
                                        React.createElement('div', { style: { fontWeight: 'bold', color: '#8b5cf6' } }, "Gift Super Fan"),
                                        React.createElement('div', { style: { fontSize: '11px', color: 'rgba(139, 92, 246, 0.8)' } }, "30 Days Access")
                                    )
                                ),
                                React.createElement('div', { style: { fontWeight: 'bold', color: 'var(--text-main)' } }, "$10.00")
                            )
                        ),


                    )
                )
            ),
            friendStatus !== 'self' && React.createElement('div', { style: { marginTop: '15px', position: 'relative', zIndex: 1 } },
                React.createElement('button', {
                    onClick: handleReport,
                    className: 'btn-ghost',
                    style: { fontSize: '10px', color: '#ef4444', opacity: 0.6, display: 'flex', alignItems: 'center', gap: '5px', margin: '0 auto' },
                    title: "Report Inappropriate Behavior"
                }, React.createElement(Flag, { size: 12 }), "Report User")
            )
        ),

        // Portfolio Section (Sparks)
        React.createElement('div', { className: 'glass-panel', style: { padding: '25px', position: 'relative', zIndex: 0 } },
            React.createElement('h3', { style: { marginBottom: '20px', fontSize: '22px' } },
                "Portfolio (", userSparks.length, " ", userSparks.length === 1 ? "spark" : "sparks", ")"
            ),
            userSparks.length === 0 ?
                React.createElement('div', { style: { textAlign: 'center', padding: '30px', color: '#999' } },
                    "No sparks yet"
                ) :
                React.createElement('div', { style: { display: 'grid', gap: '15px' } },
                    userSparks.map(spark => {
                        const isLiked = spark.likedBy && currentUser && spark.likedBy.includes(currentUser.uid);
                        const isAuthor = currentUser?.uid && spark.authorId === currentUser.uid; // Note: spark.authorId should match profileUser.id generally, but safety check

                        return React.createElement('div', {
                            key: spark.id,
                            className: 'glass-panel',
                            style: { padding: '20px', background: 'rgba(255,255,255,0.05)', position: 'relative' }
                        },
                            // Header (Timestamp + Badges) - No Name/Avatar
                            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' } },
                                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                                    React.createElement('span', { style: { fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' } },
                                        React.createElement(Clock, { size: 12 }),
                                        spark.createdAt?.seconds ? new Date(spark.createdAt.seconds * 1000).toLocaleDateString() : "Just now"
                                    ),
                                    // FIRST TO SPARK BADGE
                                    spark.isFirstSpark && React.createElement('div', {
                                        title: '🏆 First to Spark Reward',
                                        style: {
                                            marginLeft: '5px',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            background: 'linear-gradient(90deg, #ec4899, #8b5cf6)',
                                            color: 'white',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '3px',
                                            boxShadow: '0 0 10px rgba(236, 72, 153, 0.4)'
                                        }
                                    }, React.createElement(Sparkles, { size: 10, fill: 'white' }), "First Spark 🏆")
                                ),
                                // Options could go here
                            ),

                            spark.content && React.createElement('p', { style: { fontSize: '15px', lineHeight: '1.5', marginBottom: '15px', whiteSpace: 'pre-wrap' } }, spark.content),

                            spark.imageUrl && React.createElement('div', { style: { marginBottom: '15px', borderRadius: '8px', overflow: 'hidden' } },
                                React.createElement('img', { src: spark.imageUrl, style: { width: '100%', height: 'auto', display: 'block' } })
                            ),

                            spark.inspiration && React.createElement('div', { style: { background: 'rgba(139, 92, 246, 0.1)', padding: '10px', borderRadius: '6px', marginBottom: '15px', borderLeft: '3px solid var(--primary)' } },
                                React.createElement('div', { style: { fontSize: '10px', color: 'var(--primary)', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '2px' } }, "Inspired by Muse"),
                                React.createElement('div', { style: { fontSize: '13px', fontStyle: 'italic', color: 'var(--text-secondary)' } }, spark.inspiration)
                            ),

                            // Emoji Reactions Bar
                            React.createElement(EmojiReactionBar, {
                                reactions: sparkReactions[spark.id] || spark.reactions || {},
                                currentUserId: currentUser?.uid,
                                onReactionToggle: (emojiId) => handleReactionToggle(spark.id, emojiId),
                                getEmoteObject: getEmoteObject, // Pass hook helper for consistent themed rendering
                                checkAccess: (id) => {
                                    if (isAuthor) return { hasAccess: false, reason: "You cannot react to your own spark." };
                                    return checkEmoteAccess(id, currentUser);
                                }
                            }),

                            // Footer Actions
                            React.createElement('div', { style: { display: 'flex', gap: '20px', marginTop: '15px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '15px' } },
                                // LIKE
                                React.createElement('button', {
                                    onClick: () => !isAuthor && handleLikeSpark(spark),
                                    title: isAuthor ? "You cannot like your own spark" : "Like",
                                    style: {
                                        background: 'none',
                                        border: 'none',
                                        cursor: isAuthor ? 'default' : 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        color: isLiked ? '#ec4899' : '#999',
                                        transition: 'all 0.2s',
                                        opacity: isAuthor ? 0.5 : 1
                                    }
                                },
                                    React.createElement(Heart, { size: 18, fill: isLiked ? '#ec4899' : 'none' }),
                                    React.createElement('span', { style: { fontSize: '14px' } }, spark.likes || 0)
                                ),
                                // EMOJI REACTION
                                React.createElement('button', {
                                    onClick: () => !isAuthor && handleOpenEmojiPicker(spark.id),
                                    title: isAuthor ? "You cannot react to your own spark" : 'React with emoji',
                                    style: {
                                        background: 'none',
                                        border: 'none',
                                        cursor: isAuthor ? 'default' : 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        color: '#999',
                                        transition: 'all 0.2s',
                                        opacity: isAuthor ? 0.5 : 1
                                    }
                                },
                                    React.createElement(Smile, { size: 18 })
                                ),
                                // SHARE
                                React.createElement('button', {
                                    onClick: () => handleShareSpark(spark),
                                    style: { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#999' }
                                },
                                    React.createElement(Share2, { size: 18 }),
                                    React.createElement('span', { style: { fontSize: '14px' } }, "Share")
                                ),
                                // REPORT (User context for now)
                                React.createElement('button', {
                                    onClick: handleReport,
                                    style: { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#999', marginLeft: 'auto' }
                                },
                                    React.createElement(Flag, { size: 16 }),
                                    // Report icon only to be subtle
                                )
                            ),

                            // Emoji Picker for this specific spark
                            showEmojiPicker === spark.id && React.createElement(EmotePicker, {
                                userTier: currentUser?.subscriptionTier || 'global',
                                mode: 'reaction',
                                onSelect: (val) => handleEmojiSelect(spark.id, val),
                                onClose: handleCloseEmojiPicker
                            })
                        );
                    })
                )
        ),


    );
}
